import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useDotBotsAuth } from "@dotbots-boutique/auth-sdk/react";

// dotSupport user manual. Served at /app-manual per DotBots guide
// 3.55.0. Reads `lang` and `screen` from the query string, fetches
// the same static markdown that /app-manual.md serves, and renders
// a minimalistic role-aware view. No user-id is ever read from the
// URL — identity comes from the SDK.
export default function AppManual() {
  const { user } = useDotBotsAuth();
  const params = new URLSearchParams(window.location.search);
  const initialSection = params.get("screen");

  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/app-manual.md")
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setMarkdown)
      .catch((e) => setError((e as Error).message));
  }, []);

  useEffect(() => {
    if (!initialSection || !markdown) return;
    // Best-effort scroll to a heading that matches the ?screen= slug.
    const id = initialSection.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [initialSection, markdown]);

  if (error) {
    return (
      <div style={{ maxWidth: 780, margin: "24px auto", padding: "0 16px" }}>
        <h1>User manual</h1>
        <p style={{ color: "var(--danger)" }}>Failed to load the manual: {error}</p>
      </div>
    );
  }

  if (!markdown) {
    return <div style={{ padding: 24 }}>Loading…</div>;
  }

  const roles = user?.roles ?? [];
  const filtered = filterByRole(markdown, roles);

  return (
    <div className="app-manual" style={{ maxWidth: 780, margin: "24px auto", padding: "0 16px" }}>
      {roles.length === 0 && (
        <div style={{ padding: 12, background: "var(--panel-2, #f8fafc)", border: "1px solid var(--border, #e5e7eb)", borderRadius: 6, marginBottom: 16 }}>
          <strong>No role assigned yet.</strong> You can read the general
          sections below, but the app itself won't open until your
          organisation's administrator gives you a role.
        </div>
      )}
      {/* Very small markdown renderer — enough for headings, lists,
          bold/italic and paragraphs. Renders as React nodes only,
          so nothing in the .md file can inject HTML. Anything richer
          is deferred to the .md route that platform tooling reads. */}
      {renderMarkdown(filtered)}
    </div>
  );
}

// Very light role filter. A heading tagged with "*(admin only)*",
// "*(agent, admin)*", etc. drops the section for readers whose
// roles don't intersect. The .md file at /app-manual.md is served
// unfiltered so platform tooling still sees the complete manual.
function filterByRole(md: string, roles: readonly string[]): string {
  const sections = md.split(/^(?=##\s)/m);
  const roleSet = new Set(roles.map((r) => r.toLowerCase()));
  return sections
    .filter((sec) => {
      const m = /^##\s+.*?\*\(([^)]*only[^)]*|[^)]+)\)\*/i.exec(sec.split("\n")[0]);
      if (!m) return true;
      const required = m[1]
        .toLowerCase()
        .replace(/only/g, "")
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (required.length === 0) return true;
      return required.some((r) => roleSet.has(r));
    })
    .join("");
}

// Absolutely minimal markdown → React nodes. Handles the subset the
// manual actually uses (# / ## / ### headings, - bullets,
// **bold**, *italic*, `code`, blank-line paragraphs, > blockquotes).
// Anything richer stays as plain text — this route is a reader, not
// a wiki engine. Output is React nodes only, so a stray HTML tag in
// the markdown file is rendered as escaped text, not as markup.

const slug = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Turn inline markdown (**bold**, *italic*, `code`) into React
// children. Anything outside these three markers is emitted as a
// plain string — no HTML injection.
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|(?<!\*)\*[^*]+\*(?!\*)|`[^`]+`)/g;
  let lastIndex = 0;
  let idx = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index));
    const token = m[0];
    const key = `${keyPrefix}-i${idx++}`;
    if (token.startsWith("**")) {
      nodes.push(<strong key={key}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else {
      nodes.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    lastIndex = m.index + token.length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

function renderMarkdown(src: string): ReactNode {
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let listBuf: string[] | null = null;
  let quoteBuf: string[] | null = null;
  let paraBuf: string[] | null = null;
  let key = 0;
  const flushList = () => {
    if (!listBuf) return;
    const items = listBuf.slice();
    blocks.push(
      <ul key={`b${key++}`}>
        {items.map((li, i) => (
          <li key={i}>{renderInline(li, `l${key}-${i}`)}</li>
        ))}
      </ul>,
    );
    listBuf = null;
  };
  const flushPara = () => {
    if (!paraBuf) return;
    const joined = paraBuf.join(" ");
    blocks.push(<p key={`b${key++}`}>{renderInline(joined, `p${key}`)}</p>);
    paraBuf = null;
  };
  const flushQuote = () => {
    if (!quoteBuf) return;
    const items = quoteBuf.slice();
    blocks.push(
      <blockquote key={`b${key++}`}>
        {items.map((q, i) => (
          <p key={i}>{renderInline(q, `q${key}-${i}`)}</p>
        ))}
      </blockquote>,
    );
    quoteBuf = null;
  };
  const flushAll = () => { flushList(); flushPara(); flushQuote(); };

  for (const raw of lines) {
    const line = raw.trimEnd();
    let m: RegExpExecArray | null;
    if ((m = /^(#{1,3})\s+(.*)$/.exec(line))) {
      flushAll();
      const level = m[1].length;
      const text = m[2];
      const id = slug(text.replace(/\*\([^)]+\)\*/g, ""));
      const children = renderInline(text, `h${key}`);
      if (level === 1) blocks.push(<h1 key={`b${key++}`} id={id}>{children}</h1>);
      else if (level === 2) blocks.push(<h2 key={`b${key++}`} id={id}>{children}</h2>);
      else blocks.push(<h3 key={`b${key++}`} id={id}>{children}</h3>);
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushPara(); flushQuote();
      if (!listBuf) listBuf = [];
      listBuf.push(line.replace(/^[-*]\s+/, ""));
      continue;
    }
    if (/^>\s?/.test(line)) {
      flushList(); flushPara();
      if (!quoteBuf) quoteBuf = [];
      quoteBuf.push(line.replace(/^>\s?/, ""));
      continue;
    }
    if (line === "") { flushAll(); continue; }
    flushList(); flushQuote();
    if (!paraBuf) paraBuf = [];
    paraBuf.push(line);
  }
  flushAll();
  return <Fragment>{blocks}</Fragment>;
}
